import type { AuthError } from '@supabase/supabase-js'

/**
 * Maps Supabase auth errors to user-friendly error messages
 * Uses error.code when available, falls back to error.message pattern matching
 */
export function getAuthErrorMessage(error: AuthError | null): string {
    if (!error) return 'An unexpected error occurred. Please try again.'

    // Check error code first (most reliable)
    if (error.code) {
        switch (error.code) {
            case 'invalid_credentials':
            case 'invalid_grant':
                return 'Invalid email or password. Please check your credentials and try again.'

            case 'user_not_found':
                return 'No account found with this email. Please sign up.'

            case 'email_not_confirmed':
                return 'Please verify your email address before signing in.'

            case 'over_request_rate_limit':
            case 'too_many_requests':
                return 'Too many login attempts. Please wait a few minutes and try again.'

            case 'weak_password':
                return 'Password is too weak. Use at least 8 characters with a mix of letters and numbers.'

            case 'email_exists':
            case 'user_already_exists':
                return 'This email is already registered. Please sign in instead.'

            case 'network_error':
            case 'fetch_error':
                return 'Unable to connect. Please check your internet connection and try again.'

            case 'otp_expired':
                return 'Verification code has expired. Please request a new one.'

            case 'otp_disabled':
                return 'Email verification is not enabled. Please contact support.'

            default:
                // Fall through to message-based matching
                break
        }
    }

    // Fall back to message-based matching for cases where code is not provided
    const message = error.message?.toLowerCase() || ''

    if (
        message.includes('invalid login credentials') ||
        message.includes('invalid credentials') ||
        message.includes('invalid password')
    ) {
        return 'Invalid email or password. Please check your credentials and try again.'
    }

    if (message.includes('email not confirmed') || message.includes('not confirmed')) {
        return 'Please verify your email address before signing in.'
    }

    if (message.includes('user not found') || message.includes('no user found')) {
        return 'No account found with this email. Please sign up.'
    }

    if (message.includes('too many requests') || message.includes('rate limit')) {
        return 'Too many attempts. Please wait a few minutes and try again.'
    }

    if (message.includes('already registered') || message.includes('already exists')) {
        return 'This email is already registered. Please sign in instead.'
    }

    if (message.includes('weak password') || message.includes('password is too weak')) {
        return 'Password is too weak. Use at least 8 characters with a mix of letters and numbers.'
    }

    if (message.includes('invalid email')) {
        return 'Please enter a valid email address.'
    }

    if (message.includes('timeout') || message.includes('504')) {
        return 'Service is temporarily unavailable. Please try again in a moment.'
    }

    if (
        message.includes('network') ||
        message.includes('failed to fetch') ||
        message.includes('connection')
    ) {
        return 'Unable to connect. Please check your internet connection and try again.'
    }

    if (message.includes('expired')) {
        return 'Your session has expired. Please try again.'
    }

    // Return the original message if no pattern matches, but make it more user-friendly
    return error.message || 'An unexpected error occurred. Please try again.'
}

/**
 * Checks if an auth error is specifically about unconfirmed email
 * Used to determine if we should send OTP verification
 */
export function isEmailNotConfirmedError(error: AuthError | null): boolean {
    if (!error) return false

    if (error.code === 'email_not_confirmed') return true

    const message = error.message?.toLowerCase() || ''
    return (
        message.includes('email not confirmed') ||
        message.includes('not confirmed') ||
        message.includes('email_not_confirmed')
    )
}

/**
 * Checks if an auth error is a timeout/service unavailable error
 */
export function isServiceUnavailableError(error: AuthError | null): boolean {
    if (!error) return false

    const message = error.message?.toLowerCase() || ''
    return (
        message.includes('timeout') ||
        message.includes('504') ||
        message.includes('503') ||
        message.includes('gateway')
    )
}
